import { Card, CardContent, Typography } from '@mui/material';
import {
    Calculate,
    NorthWest,
    SouthEast,
    VaccinesOutlined,
    PublishedWithChangesOutlined,
    DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../application/router/routes';
import FeatureGuard from '../../common/components/FeatureGuard.tsx';
import { FEATURE_FLAGS } from '../../common/config/featureFlags.ts';
import { useFeatureFlags } from '../../common/hooks/useFeatureFlags';

const TransactionOverview = () => {
    const navigate = useNavigate();
    const { canAccess } = useFeatureFlags();

    const actions = [
        {
            label: 'Uttag',
            subtitle: 'Ta ut läkemedel från lager',
            icon: <NorthWest sx={{ fontSize: 64 }} />,
            color: 'checkout-icon',
            link: NavigationRoutes.withdrawal,
        },
        {
            label: 'Tillförsel',
            subtitle: 'Fyll på läkemedel i LM-skåp/rum/väska',
            icon: <SouthEast sx={{ fontSize: 64 }} />,
            color: 'checkin-icon',
            link: NavigationRoutes.deposit,
        },
        {
            label: 'Inventering LM-skåp/rum',
            subtitle: 'Räkna lagersaldo',
            icon: <Calculate sx={{ fontSize: 64 }} />,
            color: 'inventory-icon',
            link: NavigationRoutes.crossCheck,
        },
        {
            label: 'Kassera',
            subtitle: 'Kassera läkemedel',
            icon: <DeleteOutlineIcon sx={{ fontSize: 64 }} />,
            color: 'checkout-icon',
            link: NavigationRoutes.disposed,
        },
        {
            label: 'Tillförsel läkemedelsväska',
            subtitle: 'Fyll på läkemedel kopplat till patientärende',
            icon: <VaccinesOutlined sx={{ fontSize: 64 }} />,
            color: 'checkout-icon',
            link: NavigationRoutes.chainedTransaction,
            featureFlag: FEATURE_FLAGS.AMBULANCE_BAG_TRANSACTION,
        },
        {
            label: 'Inventering läkemedelsväska',
            subtitle: 'Räkna innehållet i LM-väska',
            icon: <PublishedWithChangesOutlined sx={{ fontSize: 64 }} />,
            color: 'inventory-icon',
            link: NavigationRoutes.shiftChangeInventory,
            featureFlag: FEATURE_FLAGS.AMBULANCE_BAG_INVENTORY_TRANSACTION,
        },
    ];

    const actionButton = (action) => {
        const cardContent = (
            <Card
                onClick={() => navigate(action.link)}
                className="hover:shadow-lg transition-shadow duration-300 h-48 flex items-center justify-center cursor-pointer w-full"
            >
                <CardContent className="flex flex-col items-center p-4">
                    <span className={`mb-2 ${action.color}`}>
                        {action.icon}
                    </span>
                    <Typography
                        variant="h4"
                        className="text-center"
                        sx={{ color: 'gray' }}
                    >
                        {action.label}
                    </Typography>
                    {action.subtitle && (
                        <Typography
                            variant="body2"
                            className="text-center"
                            sx={{ color: 'text.secondary', mt: 0.5 }}
                        >
                            {action.subtitle}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        );

        // If action has a feature flag, wrap it with FeatureGuard
        if (action.featureFlag) {
            return (
                <FeatureGuard featureKey={action.featureFlag}>
                    {cardContent}
                </FeatureGuard>
            );
        }

        // Otherwise return the card directly
        return cardContent;
    };

    // Count visible actions to determine grid layout
    const visibleActionsCount = actions.filter(
        (action) => !action.featureFlag || canAccess(action.featureFlag)
    ).length;

    // Use 2 columns for 4 buttons (2x2 layout), 3 columns for 5-6 buttons
    const gridColsClass =
        visibleActionsCount === 4
            ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2'
            : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';

    return (
        <div className="w-full px-4 py-8 mx-auto max-w-6xl">
            <h1 className="text-3xl font-bold text-center mb-16 text-gray-600">
                Förbrukningsjournal
            </h1>
            <div className={`grid ${gridColsClass} gap-4 w-full`}>
                {actions.map((action, index) => (
                    <div key={index} className="col-span-1 w-full">
                        {actionButton(action)}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TransactionOverview;
